import { useAction } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { events } from '@/models/analytics/analytics-events'
import { rootStore } from '@/models'
import { useGameQuery } from '@/hooks/convex/useGameState'
import { useActionAdvisor } from '../../screens/GameContainer/useActionAdvisor'
import { getAdvisorCoachingLevel, useActionAdvisorStore } from '../../screens/GameContainer/useActionAdvisorStore'
import { formatPlayDate } from '../../utils/dateFormatter'

// Client-local play time rides along with every action so badge date/hour
// signals (play streaks, night owl) use the player's local clock, matching
// the Daily 10 / CTH pattern of client-provided dates.
const localTime = () => ({ localDate: formatPlayDate(), localHour: new Date().getHours() })

export function usePlayerActions(gameId: Id<'games'> | undefined, userId?: string) {
  const executeAction = useAction(api.players.actions.executePlayerAction)
  const { availableActions, isPaused, game } = useGameQuery(gameId, userId)
  const { advisorContext, setFeedback, clearFeedback } = useActionAdvisor({
    gameId,
    handNumber: game?.handNumber,
    mode: game?.mode,
    userId
  })

  const executePlayerAction = async (
    action:
      | { type: 'fold' }
      | { type: 'call' }
      | { type: 'check' }
      | { type: 'allin' }
      | { type: 'bet'; amount: number }
      | { type: 'raise'; amount: number }
  ) => {
    if (!gameId || isPaused || !userId) return

    const requestScope = {
      gameId,
      handNumber: game?.handNumber,
      userId
    }

    // Capture the pending turn-start advice BEFORE clearing: the action's
    // grade is that advice's follow-up (brief confirmation when followed,
    // deviation copy when not), and the clear below would otherwise destroy
    // the context that keeps the follow-up brief instead of a full
    // re-explanation of the same spot.
    const pendingAdvice = useActionAdvisorStore.getState().advice

    clearFeedback()

    const result = await executeAction({
      gameId,
      userId,
      action,
      advisorContext,
      ...localTime()
    })

    // advisorContext is only sent when the advisor is available AND feedback is
    // enabled, so a returned bubble implies both were on at request time. Re-check
    // here too so a mid-flight toggle-off doesn't resurrect a stale bubble.
    // The feedback's `decision.street` is server-authoritative — it was computed
    // from the board the player acted on — so it is stored as-is.
    const store = useActionAdvisorStore.getState()
    if (result.advisorFeedback && store.enabled && store.feedbackEnabled) {
      const adviceOutcome = setFeedback(result.advisorFeedback, requestScope, Date.now(), pendingAdvice)
      // Analytics: the post-action grade landing — the intermediate path's
      // counterpart to advice_shown. Fires for EVERY graded action while the
      // advisor is on (including grades that followed shown advice, whose
      // messageKind is 'confirmed' — those show the thumbs-up badge, not a
      // bubble, but are still advisor output the player sees).
      rootStore?.analytics.setEvent(events.advisor.feedback_shown, {
        coachingLevel: getAdvisorCoachingLevel(store),
        gameMode: game?.mode,
        grade: result.advisorFeedback.grade,
        street: result.advisorFeedback.decision.street,
        concept: result.advisorFeedback.decision.concept,
        messageKind: result.advisorFeedback.conversation?.kind ?? 'grade'
      })
      // Analytics: how the player resolved the turn-start advice they were
      // shown for this spot (undefined when no advice was pending here).
      if (adviceOutcome) {
        rootStore?.analytics.setEvent(
          adviceOutcome === 'followed' ? events.advisor.advice_followed : events.advisor.advice_deviated,
          {
            coachingLevel: getAdvisorCoachingLevel(store),
            gameMode: game?.mode,
            street: result.advisorFeedback.decision.street,
            concept: result.advisorFeedback.decision.concept
          }
        )
      }
      return
    }

    clearFeedback()
  }

  return {
    onFold: async () => {
      await executePlayerAction({ type: 'fold' })
    },
    onCall: async () => {
      await executePlayerAction({ type: 'call' })
    },
    onCheck: async () => {
      await executePlayerAction({ type: 'check' })
    },
    onAllIn: async () => {
      await executePlayerAction({ type: 'allin' })
    },
    onBet: async (amount: number) => {
      const actionType = availableActions.includes('raise') ? 'raise' : 'bet'
      await executePlayerAction({ type: actionType, amount })
    }
  }
}
